import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { VideoLessonsTableProps } from '../types';

export function VideoLessonsTable({
  isDark,
  videoLessons,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  formatDate,
}: VideoLessonsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow
          className={cn(
            isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
          )}
        >
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Сортировка
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Название
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Тип
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            URL
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Обновлён
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Статус
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Действия
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {videoLessons.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              Видеоуроки не найдены
            </TableCell>
          </TableRow>
        ) : (
          videoLessons.map((lesson) => (
            <TableRow
              key={lesson.id}
              className={cn(
                'transition-colors',
                isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
                // Выключенный урок приглушаем: видно, что в приложении его нет.
                !lesson.is_active && 'opacity-60',
              )}
            >
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                {lesson.sort_id}
              </TableCell>
              <TableCell
                className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}
              >
                {lesson.title}
              </TableCell>
              <TableCell className={cn('uppercase text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}>
                {lesson.type}
              </TableCell>
              <TableCell className={cn('max-w-[260px] truncate', isDark ? 'text-slate-300' : 'text-slate-600')}>
                {lesson.url}
              </TableCell>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>
                {formatDate(lesson.updated_at)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={lesson.is_active}
                    onCheckedChange={() => onToggleActive(lesson)}
                    aria-label="Показывать в приложении"
                  />
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px]',
                      lesson.is_active
                        ? 'border-emerald-500 text-emerald-500'
                        : 'border-slate-400 text-slate-400',
                    )}
                  >
                    {lesson.is_active ? 'В приложении' : 'Скрыт'}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Редактировать видеоурок"
                    onClick={() => onEdit(lesson)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Удалить видеоурок"
                    onClick={() => onDelete(lesson.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-red-600'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
